import { Marked } from 'marked'

/**
 * Markdown → HTML renderer for Smart Chat replies. Backed by `marked` with
 * GFM enabled (tables, strikethrough, task lists) plus a small set of overrides:
 *
 *   • Tables carry `class="bk-chat-md-table"` so the branded gradient header
 *     styling in globals.css applies without changing any component CSS.
 *   • Links open in a new tab with `rel="noopener noreferrer"`.
 *   • Images are stripped - charts/tables are rendered as structured parts
 *     by PartsRenderer, the model occasionally leaks a redundant image link
 *     into the text and we don't want a broken placeholder.
 *
 * Output is always passed through DOMPurify (`lib/html.ts`) before rendering.
 */
const marked = new Marked({
  gfm: true,
  breaks: true,
})

marked.use({
  renderer: {
    table(token) {
      const header = token.header
        .map((cell) => {
          const align = cell.align ? ` style="text-align:${cell.align}"` : ''
          return `<th${align}>${this.parser.parseInline(cell.tokens)}</th>`
        })
        .join('')
      const body = token.rows
        .map((row) => {
          const cells = row
            .map((cell) => {
              const align = cell.align ? ` style="text-align:${cell.align}"` : ''
              return `<td${align}>${this.parser.parseInline(cell.tokens)}</td>`
            })
            .join('')
          return `<tr>${cells}</tr>`
        })
        .join('')
      return `<table class="bk-chat-md-table"><thead><tr>${header}</tr></thead><tbody>${body}</tbody></table>`
    },
    link(token) {
      const title = token.title ? ` title="${token.title}"` : ''
      const text = this.parser.parseInline(token.tokens)
      return `<a href="${token.href}"${title} target="_blank" rel="noopener noreferrer">${text}</a>`
    },
    image() {
      // Drop images - PartsRenderer owns charts/tables, stray `![](…)` in the
      // text would render as a broken placeholder.
      return ''
    },
  },
})

export function renderChatMarkdown(src: string): string {
  if (!src) return ''
  // Drop raw HTML table fragments that sometimes leak alongside a
  // render_table / render_chart tool call - structured parts are the source
  // of truth for tables/charts, a literal `<table>` wall of text is ugly.
  const pre = src
    .replace(/<table[\s\S]*?<\/table>/gi, '')
    .replace(/<thead[\s\S]*?<\/thead>/gi, '')
    .replace(/<tbody[\s\S]*?<\/tbody>/gi, '')
    .replace(/<tr[\s\S]*?<\/tr>/gi, '')
  return marked.parse(pre, { async: false }) as string
}
